import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';
import { MaterialModule } from '../../material.module';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import {
    DashboardTreeService,
    CreateNewPartLookupSlurry,
    CreateNewPartLookupBusinessUnit,
    CreateNewPartLookupWell,
    CreateNewPartRequest,
} from '../../services/dashboard-tree.service';

export interface CreateNewPartDialogData {
    projectId: number;
    projectName: string;
    isCopyingTheProject?: boolean;
}

export interface CreateNewPartForm {
    duplicateSlurry: boolean;
    slurryId: number | null;
    redesignSlurry: boolean;
    redesignReason: 'Compliance' | 'Logistics' | 'Cost' | 'WellObjective' | null;
    redesignProject: boolean;
    projectRedesignReason: 'WellObjective' | null;
    comments: string;
    wellType: 'existing' | 'new';
    wellId: number | null;
    businessUnitCode: string | null;
    wellLocation: string;
    wellName: string;
}

@Component({
    selector: 'app-create-new-part-dialog',
    standalone: true,
    imports: [MaterialModule, FormsModule, SpinnerComponent],
    templateUrl: './create-new-part-dialog.component.html',
    styleUrls: ['./create-new-part-dialog.component.css'],
})
export class CreateNewPartDialogComponent implements OnInit {
    slurries: CreateNewPartLookupSlurry[] = [];
    businessUnits: CreateNewPartLookupBusinessUnit[] = [];
    wells: CreateNewPartLookupWell[] = [];
    isLoading = true;
    isSaving = false;

    selectedTests: Record<string, boolean> = {};

    // Maps each compliance test name to its backend enum value.
    // Update these numbers to match the server-side ComplianceTestType enum.
    readonly complianceTestTypeMap: Record<string, number> = {
        'Thickening Time': 1,   // ThickeningTime
        'Fluid Loss': 2,   // FluidLoss
        'Free Fluid': 3,   // FreeFluid
        'Rheology': 4,   // Rheology
        'HPHT Rheology': 5,   // HpHtRheology
        'Static Gel Strength': 6,   // StaticGelStrength
        'Foam Stability (Cement)': 7,   // FoamStabilityCement
        'Foam Stability (Slurry)': 8,   // FoamStabilitySlurry
        'Pressurized Mud Scale': 10,   // PressurizedMudScale
        'Api Sedimentation': 12,   // ApiSedimentationInput
        'Expansion': 13,   // Expansion
        'Shear Bond': 14,   // ShearBond
        'Acid Solubility': 15,   // AcidSolubility
        'Basic Crush': 16,   // BasicCrush
        'Splitting TS': 17,   // SplittingTs
        'Unconfined CS': 18,   // UnconfinedCs
        'Triaxial Confined CS': 19,   // TriaxilConfinedCs
        'Compressive Strength (UCA)': 20,   // CompStrengthUca
        'Compressive Strength (Crush)': 21,   // CompStrengthCrush
        'Mixability': 33,   // Mixability
        'Compressive Strength (Conditioning)': 34, // CompStrengthConditionedUCA
    };

    readonly complianceTestColumns: string[][] = [
        [
            'Thickening Time',
            'Rheology',
            'Foam Stability (Cement)',
            'Pyconometer',
            'Shear Bond',
            'Splitting TS',
            'Compressive Strength (UCA)',
            'Mixability',
        ],
        [
            'Fluid Loss',
            'HPHT Rheology',
            'Foam Stability (Slurry)',
            'Api Sedimentation',
            'Acid Solubility',
            'Unconfined CS',
            'Compressive Strength (Crush)',
        ],
        [
            'Free Fluid',
            'Static Gel Strength',
            'Pressurized Mud Scale',
            'Expansion',
            'Basic Crush',
            'Triaxial Confined CS',
            'Compressive Strength (Conditioning)',
        ],
    ];

    form: CreateNewPartForm = {
        duplicateSlurry: false,
        slurryId: null,
        redesignSlurry: false,
        redesignReason: null,
        comments: '',
        wellType: 'existing',
        wellId: null,
        businessUnitCode: null,
        wellLocation: '',
        wellName: '',
        redesignProject: false,
        projectRedesignReason: null,
    };

    constructor(
        public dialogRef: MatDialogRef<CreateNewPartDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: CreateNewPartDialogData,
        private service: DashboardTreeService
    ) { }

    ngOnInit(): void {
        this.service.getCreateNewPartLookups(this.data.projectId).subscribe({
            next: (lookups) => {
                this.slurries = lookups.slurries;
                this.businessUnits = lookups.businessUnits;
                this.wells = lookups.wells;
                if (lookups.currentWellId) {
                    this.form.wellId = lookups.currentWellId;
                }
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
            },
        });
    }

    onDuplicateSlurryChange(): void {
        if (!this.form.duplicateSlurry) {
            this.form.slurryId = null;
            this.form.redesignSlurry = false;
            this.form.redesignReason = null;
            this.form.comments = '';
            this.selectedTests = {};
        }
    }

    onRedesignSlurryChange(): void {
        if (!this.form.redesignSlurry) {
            this.form.redesignReason = null;
            this.form.comments = '';
            this.selectedTests = {};
        }
    }

    onRedesignProjectChange(): void {
        if (!this.form.redesignProject) {
            this.form.projectRedesignReason = null;
            this.form.comments = '';
            this.selectedTests = {};
        }
    }

    onOk(): void {
        const isNew = this.form.wellType === 'new';
        const request: CreateNewPartRequest = {
            projectId: this.data.projectId,
            isDuplicate: !this.data.isCopyingTheProject,
            duplicateSlurry: this.form.duplicateSlurry,
            slurryToDuplicateId: this.form.duplicateSlurry ? this.form.slurryId! : undefined,
            slurryRedesignReason: this.form.redesignSlurry ? {
                reasonTypeId: this.getSlurryRedesignReasonType(this.form.redesignReason!),
                comments: this.form.comments,
                complianceTestIds: this.getSelectedComplianceTestIds(),
            } : undefined,
            projectRedesignReason: this.form.redesignProject ? {
                reasonTypeId: this.getProjectRedesignReasonType(this.form.projectRedesignReason!),
                comments: this.form.comments,
            } : undefined,
            isExistingWell: !isNew,
            existingWellId: !isNew ? this.form.wellId! : undefined,
            sbuCode: isNew ? this.form.businessUnitCode! : undefined,
            wellLocation: isNew ? this.form.wellLocation : this.wells.find(w => w.id === this.form.wellId)?.wellLocation,
            newWellName: isNew ? this.form.wellName : this.wells.find(w => w.id === this.form.wellId)?.name,
        };

        this.isSaving = true;
        this.service.createNewProjectPart(request).subscribe({
            next: (response: any) => {
                this.isSaving = false;
                this.dialogRef.close({
                    created: true,
                    createdProjectId: response?.newProjectId ?? undefined,
                    createdProjectUfId: response?.newProjectUfId ?? undefined,
                    wellId: response?.wellId ?? undefined,
                });
            },
            error: () => {
                this.isSaving = false;
            },
        });
    }

    private getSlurryRedesignReasonType(reason: 'Compliance' | 'Logistics' | 'Cost' | 'WellObjective'): number {
        switch (reason) {
            case 'Compliance': return 1;
            case 'Logistics': return 2;
            case 'Cost': return 3;
            case 'WellObjective': return 4;
        }
    }

    private getProjectRedesignReasonType(reason: 'WellObjective'): number {
        switch (reason) {
            case 'WellObjective': return 1;
        }
    }

    private getSelectedComplianceTestIds(): number[] {
        return Object.keys(this.selectedTests)
            .filter(k => this.selectedTests[k])
            .map(k => this.complianceTestTypeMap[k])
            .filter((id): id is number => id != null);
    }

    get isFormValid(): boolean {
        // Well section
        if (this.form.wellType === 'existing' && !this.form.wellId) return false;
        if (this.form.wellType === 'new') {
            if (!this.form.businessUnitCode || !this.form.wellLocation?.trim() || !this.form.wellName?.trim()) return false;
        }

        // Redesign section (Copy Project top-level)
        if (this.data.isCopyingTheProject && this.form.redesignSlurry && !this.form.redesignReason) return false;

        // Duplicate slurry section
        if (this.form.duplicateSlurry) {
            if (!this.form.slurryId) return false;
            if (this.form.redesignSlurry && !this.form.redesignReason) return false;
        }

        return true;
    }

    onCancel(): void {
        this.dialogRef.close(null);
    }
}
